import { useCallback } from 'react';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteValuationPersonInput } from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { toastError } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { removeCacheArrayItem } from '@dealstack/frontend/shared/dataExchange/cacheHelpers';
import { VALUATION_SAVE_MUTATION_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';

interface UsePersonInputDeleteArgs {
	dealId: string | undefined;
	valuationId: string | undefined;
	mipId: string | undefined;
	queryKey: QueryKey;
	reloadSummary: () => Promise<unknown>;
}

export function usePersonInputDelete({
	dealId,
	valuationId,
	mipId,
	queryKey,
	reloadSummary,
}: UsePersonInputDeleteArgs) {
	const queryClient = useQueryClient();

	const deletePersonInput = useMutation({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		mutationFn: ({
			personId: pid,
			inputId,
			mipId: deleteMipId,
		}: {
			personId: string;
			inputId: string;
			mipId?: string;
		}) => {
			return deleteValuationPersonInput({
				valuationId: valuationId ?? '',
				dealId: dealId ?? '',
				personId: pid,
				inputId,
				mipId: deleteMipId,
			});
		},
		onMutate: async ({ inputId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previousInputs = queryClient.getQueryData(queryKey);
			removeCacheArrayItem<{ id: string }>(queryClient, queryKey, inputId);
			return { previousInputs };
		},
		onSuccess: async () => {
			await reloadSummary();
		},
		onError: (error, _variables, context) => {
			if (context?.previousInputs) {
				queryClient.setQueryData(queryKey, context.previousInputs);
			}
			toastError('Error deleting field', error);
		},
	});

	const deleteField = useCallback(
		(pid: string, inputId: string) => {
			if (!valuationId || !dealId) {
				return Promise.resolve();
			}
			const isServerID = !inputId.includes('local');
			if (!isServerID) {
				return Promise.resolve();
			}
			return deletePersonInput.mutateAsync({ personId: pid, inputId, mipId });
		},
		[valuationId, dealId, deletePersonInput, mipId]
	);

	return { deleteField };
}
